import type { CamelCaseSubmodel } from '@/apis/server/new-car/v1/car-detail';
import type {
  CamelCasePromotion,
  CamelCasePromotionList,
} from '@/apis/server/new-car/v1/promotions';
import type {
  PriceRange,
} from '@/app/(routes)/listing/_components/page-content/search-list/car-card/types';// @suppress longLineCheck

export const getPriceRange = (
  submodels: CamelCaseSubmodel[],
): PriceRange => {
  const validPrices = submodels
    .filter(({ isAvailable }) => isAvailable)
    .map(({ price }) => price)
    .filter((price): price is number => price !== null);

  if (validPrices.length === 0) return { lowest: null, highest: null };

  return {
    lowest: Math.min(...validPrices),
    highest: Math.max(...validPrices),
  };
};

export const getMinPrice = (
  submodels: CamelCaseSubmodel[],
): number | null => {
  const validPrices = submodels
    .filter(({ isAvailable }) => isAvailable)
    .map(({ price }) => price)
    .filter((price): price is number => price !== null);

  if (validPrices.length === 0) return null;

  return Math.min(...validPrices);
};

export const getAveragePrice = (
  submodels: CamelCaseSubmodel[],
): number => {
  const validPrices = submodels
    .filter(({ isAvailable }) => isAvailable)
    .map(({ price }) => price)
    .filter((price): price is number => price !== null);

  if (validPrices.length === 0) return 0;

  const total = validPrices.reduce((sum, price) => sum + price, 0);
  return total / validPrices.length;
};

export const getFaceliftDate = (
  submodels: CamelCaseSubmodel[],
): string | null => submodels.reduce<string | null>((closest, { faceliftDate }) => { // @suppress longLineCheck
  if (!faceliftDate) return closest;

  return !closest
    || new Date(faceliftDate) > new Date(closest)
    ? faceliftDate
    : closest;
}, null);

export const getMatchStatusSubModels = (
  isAvailable: boolean,
  submodels: CamelCaseSubmodel[],
): CamelCaseSubmodel[] => (isAvailable
  ? submodels.filter(model => model.isAvailable)
  : submodels.filter(model => !model.isAvailable));

export const shuffleArray = <T>(array: T[]): T[] => {
  const deepCopy: T[] = JSON.parse(JSON.stringify(array));
  const shuffled = [...deepCopy];

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));

    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const getCompetitorPromotions = (
  promotions: CamelCasePromotionList,
  carId: number,
): CamelCasePromotion | null => promotions
  .find(
    promotion => promotion.promoType === 'tactical_ads'
    && promotion.competitorCars?.find(
      id => id === carId.toString(),
    ),
  ) || null;
